import { useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import {
  DateInput,
  Flex,
  Input,
  InputTextArea,
  Modal,
  Select,
  Spinner,
  Text,
} from '@/lib/v2/components';
import { eventOnChangeDateInput } from '@/lib/v2/components/DateInput/DateInput';
import { DropdownItemProps } from '@/lib/v2/components/Dropdown';
import { Option } from '@/lib/v2/components/Select/Select';

import { ButtonActions } from './components/ButtonActions';
import { STRATEGY_MAP_BY_ID, TRANSLATE_MODAL, TRANSLATE_MODAL_INPUTS } from './constants';

import {
  atomDetailCampaign,
  atomLoadingDetail,
  atomRefetchAllData,
  atomRefetchDetailData,
  atomStrategyList,
} from '@/modules/CampaignsModule/atoms/campaignAtom';
import {
  atomModalTypeProcess,
  atomToggleEditCreateCampaignModal,
} from '@/modules/CampaignsModule/atoms/modalsCampaignAtom';
import { useCampaign } from '@/modules/CampaignsModule/hooks/useCampaigns';
import {
  EMaxMinLength,
  IFormInput,
  ModalType,
} from '@/modules/CampaignsModule/interfaces/Campaigns';
import { strategyOrderOtherOption } from '@/modules/CampaignsModule/utils';

interface ICreateEditCampaignsProps {
  onClose: (value?: boolean) => void;
}

const CreateEditCampaigns = ({ onClose }: ICreateEditCampaignsProps) => {
  const { t } = useTranslation();
  const { createCampaign, loading } = useCampaign();
  const { control, handleSubmit, setValue, reset } = useForm<IFormInput>();

  const toggleEditCategoryModal = useAtomValue(atomToggleEditCreateCampaignModal);
  const strategyList = useAtomValue(atomStrategyList);
  const detailCampaign = useAtomValue(atomDetailCampaign);
  const modalTypeProcess = useAtomValue(atomModalTypeProcess);
  const loadingDetail = useAtomValue(atomLoadingDetail);

  const setRefetchAllData = useSetAtom(atomRefetchAllData);
  const setRefetchDetailData = useSetAtom(atomRefetchDetailData);

  const [hasOtherStrategy, setHasOtherStrategy] = useState<boolean>(false);
  const [strategiesList, setStrategiesList] = useState<DropdownItemProps[]>([]);
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
    detailCampaign?.startDate ? new Date(detailCampaign.startDate) : null,
    detailCampaign?.endDate ? new Date(detailCampaign.endDate) : null,
  ]);
  const [startDate, endDate] = dateRange;

  // eslint-disable-next-line regexp/no-obscure-range
  const REGEX_INPUT = /^[\dA-Za-zÀ-ÖØ-öø-ÿ\s\-,'.!¡¿?()\\"&$%]+$/u;
  // eslint-disable-next-line regexp/no-obscure-range
  const REGEX_OTHER_STRATEGY = /^[\dA-Za-zÀ-ÖØ-öø-ÿ ]+$/;

  const VALIDATION_RULES = {
    name: {
      pattern: {
        value: REGEX_INPUT,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.pattern`),
      },
      required: {
        value: true,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.required`),
      },
      minLength: {
        value: EMaxMinLength.MIN_LENGTH,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.minLength`, {
          name: t(`${TRANSLATE_MODAL_INPUTS}.name.label`).toLowerCase(),
          min: EMaxMinLength.MIN_LENGTH,
        }),
      },
      maxLength: {
        value: EMaxMinLength.NAME_MAX,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.maxLength`, {
          name: t(`${TRANSLATE_MODAL_INPUTS}.name.label`).toLowerCase(),
          max: EMaxMinLength.NAME_MAX,
        }),
      },
    },
    objective: {
      pattern: {
        value: REGEX_INPUT,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.pattern`),
      },
      required: {
        value: true,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.required`),
      },
      minLength: {
        value: EMaxMinLength.MIN_LENGTH,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.minLength`, {
          name: t(`${TRANSLATE_MODAL_INPUTS}.objective.label`).toLowerCase(),
          min: EMaxMinLength.MIN_LENGTH,
        }),
      },
      maxLength: {
        value: EMaxMinLength.OBJECTIVE_MAX,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.maxLength`, {
          name: t(`${TRANSLATE_MODAL_INPUTS}.objective.label`).toLowerCase(),
          max: EMaxMinLength.OBJECTIVE_MAX,
        }),
      },
    },
    strategy: {
      required: {
        value: true,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.required`),
      },
    },
    otherStrategy: {
      pattern: {
        value: REGEX_OTHER_STRATEGY,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.pattern`),
      },
      required: {
        value: true,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.required`),
      },
      minLength: {
        value: EMaxMinLength.MIN_LENGTH,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.minLength`, {
          name: t(`${TRANSLATE_MODAL_INPUTS}.otherStrategy.label`).toLowerCase(),
          min: EMaxMinLength.MIN_LENGTH,
        }),
      },
      maxLength: {
        value: EMaxMinLength.OTHER_STRATEGY_MAX,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.maxLength`, {
          name: t(`${TRANSLATE_MODAL_INPUTS}.otherStrategy.label`).toLowerCase(),
          max: EMaxMinLength.OTHER_STRATEGY_MAX,
        }),
      },
    },
    date: {
      validate: (value: Date[]) => {
        if (value && Array.isArray(value)) {
          const [startDate, endDate] = value as [Date | null, Date | null];
          if (!startDate || !endDate) return t(`${TRANSLATE_MODAL}.INPUTS_ERROR.dateRange`);
        }
      },
      required: {
        value: true,
        message: t(`${TRANSLATE_MODAL}.INPUTS_ERROR.required`),
      },
    },
  };

  useEffect(() => {
    const hasOtherStrategyOption = true;
    const strategiesListCast = strategyOrderOtherOption(strategyList, hasOtherStrategyOption);
    setStrategiesList(strategiesListCast);
  }, [strategyList]);

  const handleChangeStrategy = useCallback((value: Option | Option[]) => {
    const data = value as Option;
    setHasOtherStrategy(data?.value === 'other');
  }, []);

  const handleCloseModal = useCallback(() => onClose(), [onClose]);

  const handleOnChangeDate: eventOnChangeDateInput = useCallback((event) => {
    if (Array.isArray(event)) setDateRange(event as [Date | null, Date | null]);
  }, []);

  const handleSubmitData = useCallback(
    async (formData: IFormInput) => {
      await createCampaign(formData);
      setRefetchAllData(true);
      setRefetchDetailData(true);
      handleCloseModal();
    },
    [createCampaign, handleCloseModal, setRefetchAllData, setRefetchDetailData]
  );

  const isUserStrategy = useMemo(() => {
    return strategiesList.find((strategy) => strategy.label === detailCampaign?.strategyName);
  }, [strategiesList, detailCampaign]);

  useEffect(() => {
    if (modalTypeProcess === ModalType.EDIT && Object.keys(detailCampaign).length > 0) {
      const { name, goal, startDate, endDate } = detailCampaign;

      setValue('name', name ?? '');
      setValue('objective', goal ?? '');
      if (startDate && endDate) {
        setDateRange([new Date(startDate), new Date(endDate)]);
        setValue('date', [new Date(startDate), new Date(endDate)]);
      }

      isUserStrategy?.id &&
        setValue('strategy', {
          id: isUserStrategy?.id ?? '',
          name: t(
            `CAMPAIGNS_MAIN.DROPDOWN_STRATEGIES.${STRATEGY_MAP_BY_ID[Number(isUserStrategy?.id)]}`,
            isUserStrategy?.label
          ),
          value: isUserStrategy?.value ?? '',
        });
    }

    return () => {
      setDateRange([null, null]);
      reset();
    };
  }, [detailCampaign, isUserStrategy, setValue, t, reset, modalTypeProcess]);

  return (
    <Modal open={toggleEditCategoryModal} onClose={handleCloseModal}>
      <div className="flex flex-col gap-2 divide-y">
        <header>
          <Text as="h3" fontWeight="medium" variant="sub-headline">
            {modalTypeProcess === ModalType.EDIT
              ? t(`${TRANSLATE_MODAL}.titleModalEdit`)
              : t(`${TRANSLATE_MODAL}.titleModal`)}
          </Text>
        </header>
        <section className="flex w-full flex-col pt-6">
          {!loadingDetail ? (
            <form
              autoComplete="off"
              id="createEditCampaigns"
              onSubmit={handleSubmit(handleSubmitData)}
            >
              <div className="grid gap-2">
                <Input
                  isRequired
                  control={control}
                  id="name"
                  label={t(`${TRANSLATE_MODAL_INPUTS}.name.label`)}
                  name="name"
                  placeHolder={t(`${TRANSLATE_MODAL_INPUTS}.name.placeholder`)}
                  rules={VALIDATION_RULES.name}
                  type="text"
                />
                <div className="w-full">
                  <InputTextArea
                    isRequired
                    control={control}
                    id="objective"
                    label={t(`${TRANSLATE_MODAL_INPUTS}.objective.label`)}
                    name="objective"
                    placeHolder={t(`${TRANSLATE_MODAL_INPUTS}.objective.placeholder`)}
                    rows={3}
                    rules={VALIDATION_RULES.objective}
                  />
                </div>
                <Select
                  isRequired
                  control={control}
                  id="strategy"
                  label={t(`${TRANSLATE_MODAL_INPUTS}.strategy.label`)}
                  name="strategy"
                  options={strategiesList
                    .map(
                      (strategy): Option => ({
                        ...strategy,
                        name: t(
                          `CAMPAIGNS_MAIN.DROPDOWN_STRATEGIES.${
                            STRATEGY_MAP_BY_ID[Number(strategy.id)]
                          }`,
                          strategy.label
                        ),
                        value: strategy.value,
                      })
                    )
                    .sort((a, b) => {
                      if (a.value === 'other') return 1;
                      if (b.value === 'other') return -1;
                      return String(a.name).localeCompare(String(b.name));
                    })}
                  placeholder={t(`${TRANSLATE_MODAL_INPUTS}.strategy.placeholder`)}
                  rules={VALIDATION_RULES.strategy}
                  onChange={handleChangeStrategy}
                />
                {hasOtherStrategy && (
                  <Input
                    isRequired
                    control={control}
                    id="otherStrategy"
                    label={t(`${TRANSLATE_MODAL_INPUTS}.otherStrategy.label`)}
                    name="otherStrategy"
                    placeHolder={t(`${TRANSLATE_MODAL_INPUTS}.otherStrategy.placeholder`)}
                    rules={VALIDATION_RULES.otherStrategy}
                    type="text"
                  />
                )}
                <DateInput
                  fullWidth
                  isRequired
                  selectsRange
                  control={control}
                  endDate={endDate}
                  id="date"
                  label={t(`${TRANSLATE_MODAL_INPUTS}.date.label`)}
                  minDate={
                    modalTypeProcess === ModalType.CREATE
                      ? new Date()
                      : detailCampaign?.startDate
                      ? new Date(detailCampaign?.startDate)
                      : null
                  }
                  name="date"
                  placeholder={t(`${TRANSLATE_MODAL_INPUTS}.date.placeholder`)}
                  rules={VALIDATION_RULES.date}
                  startDate={startDate}
                  onChange={handleOnChangeDate}
                />
              </div>
              <ButtonActions
                isLoading={loading}
                textCancelButton={t(`${TRANSLATE_MODAL}.cancelButton`)}
                textSubmitButton={
                  modalTypeProcess === ModalType.EDIT
                    ? t(`${TRANSLATE_MODAL}.updateButton`)
                    : t(`${TRANSLATE_MODAL}.successButton`)
                }
                onClose={handleCloseModal}
              />
            </form>
          ) : (
            <Flex alignment="center" className="min-h-[372px]">
              <Spinner withoutOverlay />
            </Flex>
          )}
        </section>
      </div>
    </Modal>
  );
};
export default CreateEditCampaigns;
